/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { isEmpty } from '@ember/utils';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';
import isValidEmail from 'cio/utils/validators/email';
import AuthService from 'cio/services/auth';
import AccountService from 'cio/services/account';
import FormValidations from 'cio/utils/form-validations';

const Validations = {
  website(value: string) {
    if (isEmpty(value.trim())) {
      return "Website can't be blank";
    }

    return true;
  },
  description(value: string) {
    if (isEmpty(value.trim())) {
      return "Description can't be blank";
    }

    return true;
  },
  technicalEmail: {
    dependentKeys: ['technicalEmailConfirm'],
    validator(value: string) {
      if (!isValidEmail(value.trim())) {
        return 'Please enter a valid email address';
      }

      return true;
    },
  },
  technicalEmailConfirm: {
    dependentKeys: ['technicalEmail'],
    validator(this: SettingsAccountVerificationForm, value: string) {
      let { technicalEmail } = this.formValues;
      if (isEmpty(value) || value !== technicalEmail) {
        return "Your technical email addresses don't match";
      }

      return true;
    },
  },
};

interface SettingsAccountVerificationFormArgs {
  onSubmit: (values: any) => unknown;
}

export default class SettingsAccountVerificationForm extends Component<SettingsAccountVerificationFormArgs> {
  @service auth!: AuthService;
  @service account!: AccountService;

  @tracked validations: FormValidations;
  @tracked isTechnicalEmailConfirmVisible = false;
  @tracked formValues = {
    website: '',
    description: '',
    technicalEmail: '',
    technicalEmailConfirm: '',
  };

  get isReadOnly() {
    return !this.account.current?.confirmed || !this.auth.hasPermission('account/edit');
  }

  get isSubmitDisabled() {
    return this.submitTask.isRunning || this.isReadOnly || this.validations.hasErrors;
  }

  constructor(owner: unknown, args: SettingsAccountVerificationFormArgs) {
    super(owner, args);

    this.validations = FormValidations.create({
      validations: Validations,
      context: this,
      modelKey: 'formValues',
    });
  }

  submitTask = task(async (event: Event) => {
    event.preventDefault();

    await this.validations.validateModel();
    if (!this.validations.hasErrors) {
      await this.args.onSubmit({
        website: this.formValues.website,
        description: this.formValues.description,
        technicalContact: this.formValues.technicalEmail.trim(),
      });
    }
  });

  @action
  onDescriptionInput(event: InputEvent & { target: HTMLInputElement }) {
    this.formValues.description = event.target.value;
    this.validations.validateAttribute('description');
  }

  @action
  onFormInput(key: string, value: string) {
    this.formValues = {
      ...this.formValues,
      [key]: value,
    };
    this.validations.validateAttribute(key);
  }

  @action
  onTechnicalEmailBlur() {
    this.isTechnicalEmailConfirmVisible = !isEmpty(this.formValues.technicalEmail);
  }
}
